import {
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import type {
    LucideIcon,
} from "lucide-preact";
import {
    AlertTriangle,
    Check,
    Copy,
    Crop,
    Edit3,
    Layers,
    Shield,
    ShieldOff,
    Sparkles,
    X,
} from "lucide-preact";
import {
    useCallback,
    useEffect,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

import type {
    ReviewCategory,
    ReviewItem,
    ReviewOverrideBody,
} from "@mrflark/spl_sdk";
import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import {
    splClient,
} from "../../api/client.ts";

import {
    Button,
    ConfirmModal,
} from "@mrflark/ui";
import {
    CropModal,
} from "../media/CropModal.tsx";
import {
    DuplicateClusterReviewView,
} from "./DuplicateClusterReviewView.tsx";
import {
    ReviewOverrideModal,
} from "./ReviewOverrideModal.tsx";

const SIMILARITY_PATTERN = /similarity:\s*([\d.]+)%/;

const parseSimilarity = (reviewReason?: string): number | null => {
    if (!reviewReason) return null;
    const match = reviewReason.match(SIMILARITY_PATTERN);
    return match ? parseFloat(match[1]) : null;
};

const SimilarityBadge = ({
    percent,
}: { percent: number; }) => {
    const colorClass = percent >= 90
        ? "bg-green-500/20 text-green-400"
        : percent >= 85
        ? "bg-yellow-500/20 text-yellow-400"
        : "bg-red-500/20 text-red-400";

    return (
        <span
            className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}
        >
            {percent.toFixed(1)}% similar
        </span>
    );
};

const ThumbnailPanel = ({
    imageUrl,
    itemId,
    label,
    description,
    tags,
}: {
    imageUrl: string | null;
    // when set, clicking the thumbnail opens the Viewer page for this
    // item instead of the raw image file. Viewer offers tag editing,
    // OCR overlay, and navigation — far more useful than a bare image
    itemId?: string;
    label: string;
    description?: string;
    tags?: string[];
}) => {
    const onClickThumbnail = (): void => {
        if (itemId) {
            globalThis.open(`/?id=${itemId}`, "_blank");
        } else if (imageUrl) {
            globalThis.open(imageUrl, "_blank");
        }
    };
    return (
        <div className="flex-1 space-y-2">
            <p className="text-xs font-medium text-text-muted uppercase tracking-wider">
                {label}
            </p>
            <div className="w-40 h-40 bg-bg-tertiary rounded overflow-hidden">
                {imageUrl
                    ? (
                        <img
                            src={imageUrl}
                            alt={label}
                            className="w-full h-full object-cover cursor-pointer"
                            onClick={onClickThumbnail}
                        />
                    )
                    : (
                        <div className="w-full h-full flex items-center justify-center text-text-muted text-sm">
                            Loading...
                        </div>
                    )}
            </div>
            {description && (
                <p className="text-xs text-text-secondary line-clamp-2">
                    {description}
                </p>
            )}
            {tags && tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {tags.slice(0, 5).map((tag) => (
                        <span
                            key={tag}
                            className="px-1.5 py-0.5 rounded text-[10px] bg-bg-tertiary text-text-muted"
                        >
                            {tag}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const ReviewItemCard = ({
    item,
    isFocused,
    onApprove,
    onReject,
    onOverride,
    onConfirmWatermark,
    onDenyWatermark,
}: {
    item: ReviewItem;
    isFocused?: boolean;
    onApprove: (id: string) => void;
    onReject: (id: string) => void;
    onOverride: (item: ReviewItem) => void;
    onConfirmWatermark: (item: ReviewItem) => void;
    onDenyWatermark: (id: string) => void;
}) => {
    // watermark-flagged items get a purpose-built action pair instead of
    // the generic approve/reject. "Has watermark" opens the crop modal
    // (so the operator can excise the platform banner) and records the
    // detector verdict as confirmed; "No watermark" records a false-
    // positive verdict, clears has_watermark, and exits the queue
    const isWatermarkFlag = item.has_watermark === true;
    const matchId = item.duplicate_of ?? item.semantic_duplicate_of;
    const isDuplicate = !!matchId;

    const imageUrl = item.thumbnail_url ?? item.media_url ?? null;

    const {
        data: originalItem,
    } = useQuery({
        queryKey: ["mediaItem", matchId],
        queryFn: () => splClient.getMediaItem(matchId!),
        enabled: isDuplicate,
    });

    const originalImageUrl = originalItem?.thumbnail_url ??
        originalItem?.media_url ?? null;
    const originalPrimary = originalItem
        ? getPrimaryAnalysis(originalItem.ai_analyses)
        : null;

    const primary = getPrimaryAnalysis(item.ai_analyses);
    const similarity = parseSimilarity(item.review_reason);

    return (
        <div
            className={`bg-bg-secondary rounded-lg border overflow-hidden ${
                isFocused ? "border-accent ring-2 ring-accent" : "border-border"
            }`}
        >
            <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            {item.review_reason ?? "Flagged for review"}
                        </span>
                        {similarity !== null && (
                            <SimilarityBadge percent={similarity} />
                        )}
                        <span className="text-xs text-text-muted">
                            {item.media_type}
                        </span>
                    </div>
                    <div className="flex-shrink-0 flex gap-2">
                        {isWatermarkFlag
                            ? (
                                <>
                                    <Button
                                        variant="error"
                                        size="sm"
                                        onClick={() => onConfirmWatermark(item)}
                                        title="Confirm the watermark and open the crop modal to remove it"
                                    >
                                        <Crop size={14} className="mr-1" />
                                        Has watermark
                                    </Button>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => onDenyWatermark(item.id)}
                                        title="Mark as a false positive — detector won't re-flag this image"
                                    >
                                        <ShieldOff size={14} className="mr-1" />
                                        No watermark
                                    </Button>
                                </>
                            )
                            : (
                                <>
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => onApprove(item.id)}
                                    >
                                        <Check size={14} className="mr-1" />
                                        Approve
                                    </Button>
                                    <Button
                                        variant="error"
                                        size="sm"
                                        onClick={() => onReject(item.id)}
                                    >
                                        <X size={14} className="mr-1" />
                                        Reject
                                    </Button>
                                </>
                            )}
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => onOverride(item)}
                        >
                            <Edit3 size={14} className="mr-1" />
                            Edit
                        </Button>
                    </div>
                </div>

                {isDuplicate
                    ? (
                        <div className="flex gap-6">
                            <ThumbnailPanel
                                imageUrl={imageUrl}
                                itemId={item.id}
                                label="Flagged"
                                description={primary?.entry.description}
                            />
                            <div className="flex items-center text-text-muted">
                                <span className="text-lg">↔</span>
                            </div>
                            <ThumbnailPanel
                                imageUrl={originalImageUrl}
                                itemId={matchId ?? undefined}
                                label="Original"
                                description={originalPrimary?.entry.description}
                                tags={originalItem?.tags}
                            />
                        </div>
                    )
                    : (
                        <div className="flex gap-4">
                            <div className="flex-shrink-0 w-40 h-40 bg-bg-tertiary rounded overflow-hidden">
                                {imageUrl
                                    ? (
                                        <img
                                            src={imageUrl}
                                            alt="Review item"
                                            className="w-full h-full object-cover cursor-pointer"
                                            onClick={() =>
                                                globalThis.open(
                                                    `/?id=${item.id}`,
                                                    "_blank",
                                                )}
                                        />
                                    )
                                    : (
                                        <div className="w-full h-full flex items-center justify-center text-text-muted text-sm">
                                            Loading...
                                        </div>
                                    )}
                            </div>
                            <div className="flex-1 space-y-2 min-w-0">
                                {primary?.entry.description && (
                                    <p className="text-sm text-text-primary line-clamp-2">
                                        {primary.entry.description}
                                    </p>
                                )}
                                <div className="flex items-center gap-4 text-xs text-text-muted">
                                    {primary?.entry.confidence !== undefined &&
                                        (
                                            <span>
                                                Confidence:{" "}
                                                {(primary.entry.confidence *
                                                    100).toFixed(0)}%
                                            </span>
                                        )}
                                    {item.has_watermark && (
                                        <span className="flex items-center gap-1 text-yellow-400">
                                            <Shield size={10} />
                                            Watermark{item.watermark_description
                                                ? `: ${item.watermark_description}`
                                                : ""}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-text-muted">
                                    {new Date(item.created_at).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
};

// pill-bar catalog for the review queue. order is intentional: All
// first as the reset target, then by triage priority — watermarked
// items are usually the largest cohort and the cheapest to dispatch
// (one-click crop+approve), duplicates next, model-flagged after, and
// "other" last as the catch-all bucket
type CategoryPillConfig = {
    key: ReviewCategory;
    label: string;
    icon: LucideIcon;
    accent: string;
};

const CATEGORY_PILLS: CategoryPillConfig[] = [
    {
        key: "all",
        label: "All",
        icon: Layers,
        accent: "text-text-primary",
    },
    {
        key: "watermark",
        label: "Watermarked",
        icon: Shield,
        accent: "text-amber-300",
    },
    {
        key: "duplicate",
        label: "Possible duplicate",
        icon: Copy,
        accent: "text-sky-300",
    },
    {
        key: "ai_flagged",
        label: "AI flagged",
        icon: Sparkles,
        accent: "text-fuchsia-300",
    },
    {
        key: "other",
        label: "Other",
        icon: AlertTriangle,
        accent: "text-text-secondary",
    },
];

const CategoryPillBar = ({
    counts,
    active,
    onSelect,
}: {
    counts: Record<ReviewCategory, number>;
    active: ReviewCategory;
    onSelect: (next: ReviewCategory) => void;
}) => {
    return (
        <div className="flex flex-wrap items-center gap-2">
            {CATEGORY_PILLS.map((pill) => {
                const Icon = pill.icon;
                const count = counts[pill.key] ?? 0;
                const isActive = active === pill.key;
                const isEmpty = count === 0 && pill.key !== "all";
                if (isEmpty) return null;
                return (
                    <button
                        type="button"
                        key={pill.key}
                        onClick={() => onSelect(pill.key)}
                        aria-pressed={isActive}
                        className={`group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-150 ${
                            isActive
                                ? "bg-accent text-white border-accent shadow-[0_0_0_3px_rgba(255,255,255,0.04)]"
                                : "bg-bg-secondary border-border text-text-secondary hover:border-accent/40 hover:text-text-primary"
                        }`}
                    >
                        <Icon
                            size={14}
                            className={isActive
                                ? "text-white"
                                : `${pill.accent} group-hover:text-text-primary`}
                        />
                        <span className="tracking-wide">{pill.label}</span>
                        <span
                            className={`ml-1 px-1.5 py-px rounded-full text-[10px] tabular-nums font-semibold ${
                                isActive
                                    ? "bg-white/20 text-white"
                                    : "bg-bg-tertiary text-text-muted"
                            }`}
                        >
                            {count}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

export const ReviewQueueTab = () => {
    const queryClient = useQueryClient();
    const [rejectingId, setRejectingId] = useState<string | null>(null);
    const [overrideItem, setOverrideItem] = useState<ReviewItem | null>(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [category, setCategory] = useState<ReviewCategory>("all");
    // crop-then-confirm flow for watermark category. cropping must
    // succeed BEFORE the verdict is recorded — otherwise the row
    // leaves the queue while the watermark is still in the pixels
    const [cropTarget, setCropTarget] = useState<ReviewItem | null>(null);

    const {
        data,
        isLoading,
    } = useQuery({
        queryKey: ["reviewItems", category],
        queryFn: () => splClient.getReviewItems(200, category),
    });

    // duplicate category gets a different shape entirely — clusters
    // instead of individual cards — so we fetch a separate endpoint
    // when that pill is active. lazy: skip the fetch unless the
    // category is "duplicate"
    const {
        data: clusterData,
        isLoading: isLoadingClusters,
    } = useQuery({
        queryKey: ["duplicateClusters"],
        queryFn: () => splClient.getDuplicateClusters(),
        enabled: category === "duplicate",
    });

    // watermark detector accuracy stat. fetched on every render so
    // the chip on the Watermarked pill stays current as the operator
    // triages — but it's a single small JSON payload + react-query
    // dedupes within the staleTime window
    const { data: watermarkAccuracy } = useQuery({
        queryKey: ["watermarkAccuracy"],
        queryFn: () => splClient.getWatermarkAccuracy(),
        staleTime: 30_000,
    });

    const invalidateReview = (): void => {
        queryClient.invalidateQueries({
            queryKey: ["reviewItems"],
        });
    };

    const approveMutation = useMutation({
        mutationFn: (itemId: string) => splClient.approveItem(itemId),
        onSuccess: () => {
            toast.success("Item approved");
            invalidateReview();
        },
        onError: (error: Error) =>
            toast.error(`Failed to approve: ${error.message}`),
    });

    const rejectMutation = useMutation({
        mutationFn: (itemId: string) => splClient.rejectItem(itemId),
        onSuccess: () => {
            toast.success("Item rejected");
            invalidateReview();
            setRejectingId(null);
        },
        onError: (error: Error) =>
            toast.error(`Failed to reject: ${error.message}`),
    });

    const overrideMutation = useMutation({
        mutationFn: ({
            itemId,
            overrides,
        }: { itemId: string; overrides: ReviewOverrideBody; }) =>
            splClient.overrideItem(itemId, overrides),
        onSuccess: () => {
            toast.success("Item updated and approved");
            invalidateReview();
            setOverrideItem(null);
        },
        onError: (error: Error) =>
            toast.error(`Failed to override: ${error.message}`),
    });

    const clearQueueMutation = useMutation({
        mutationFn: () => splClient.clearReviewQueue(),
        onSuccess: () => {
            toast.success("Review queue cleared — all items approved");
            invalidateReview();
            setShowClearConfirm(false);
        },
        onError: (error: Error) =>
            toast.error(`Failed to clear queue: ${error.message}`),
    });

    const confirmWatermarkMutation = useMutation({
        mutationFn: (id: string) => splClient.confirmWatermark(id),
        onSuccess: () => {
            toast.success("Watermark confirmed");
            invalidateReview();
            setCropTarget(null);
        },
        onError: (error: Error) =>
            toast.error(`Failed to confirm watermark: ${error.message}`),
    });

    const denyWatermarkMutation = useMutation({
        mutationFn: (id: string) => splClient.denyWatermark(id),
        onSuccess: () => {
            toast.success("Marked as no watermark — detector will skip");
            invalidateReview();
        },
        onError: (error: Error) =>
            toast.error(`Failed to record verdict: ${error.message}`),
    });

    const items = data?.items ?? [];

    const [focusedIndex, setFocusedIndex] = useState(-1);

    useEffect(() => {
        setFocusedIndex(-1);
    }, [data]);

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        const target = event.target as HTMLElement;
        if (
            target.tagName === "INPUT" || target.tagName === "TEXTAREA" ||
            target.isContentEditable
        ) return;

        if (event.key === "j" || event.key === "ArrowDown") {
            event.preventDefault();
            setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
        } else if (event.key === "k" || event.key === "ArrowUp") {
            event.preventDefault();
            setFocusedIndex((prev) => Math.max(prev - 1, 0));
        } else if (
            event.key === "a" && focusedIndex >= 0 &&
            focusedIndex < items.length
        ) {
            event.preventDefault();
            approveMutation.mutate(items[focusedIndex].id);
        } else if (
            event.key === "x" && focusedIndex >= 0 &&
            focusedIndex < items.length
        ) {
            event.preventDefault();
            setRejectingId(items[focusedIndex].id);
        } else if (event.key === "Escape") {
            setFocusedIndex(-1);
        }
    }, [items, focusedIndex, approveMutation]);

    useEffect(() => {
        globalThis.addEventListener("keydown", handleKeyDown);
        return () => globalThis.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const handleOverrideSave = (overrides: ReviewOverrideBody): void => {
        if (!overrideItem) return;
        overrideMutation.mutate({
            itemId: overrideItem.id,
            overrides,
        });
    };

    const categoryCounts = data?.category_counts ?? {
        all: 0,
        watermark: 0,
        duplicate: 0,
        ai_flagged: 0,
        other: 0,
    };
    // count for the "Approve All" CTA mirrors what's currently in view —
    // approving while filtered to "watermark" should not silently bulk-
    // approve unrelated items; the clear endpoint operates on the whole
    // REVIEW set so we only show the bulk action when "All" is selected
    const showBulkApprove = category === "all" && items.length > 0;

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-4">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex flex-col gap-3 min-w-0">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold">Review Queue</h2>
                        {data && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-400">
                                {data.total_count} {category === "all"
                                    ? `item${data.total_count !== 1 ? "s" : ""}`
                                    : "in view"}
                            </span>
                        )}
                    </div>
                    <CategoryPillBar
                        counts={categoryCounts}
                        active={category}
                        onSelect={setCategory}
                    />
                    {category === "watermark" && watermarkAccuracy &&
                        watermarkAccuracy.total > 0 && (
                        <div className="text-xs text-text-muted">
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/20">
                                <Shield size={11} className="text-amber-300" />
                                <span className="text-amber-300 font-medium">
                                    {watermarkAccuracy.accuracy !== null
                                        ? `${
                                            (watermarkAccuracy.accuracy * 100)
                                                .toFixed(0)
                                        }% detector accuracy`
                                        : "no verdicts yet"}
                                </span>
                                <span className="text-text-muted">
                                    · {watermarkAccuracy.confirmed} confirmed,
                                    {" "}
                                    {watermarkAccuracy.false_positive}{" "}
                                    false positives
                                </span>
                            </span>
                        </div>
                    )}
                </div>
                {showBulkApprove && (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowClearConfirm(true)}
                    >
                        <Check size={14} className="mr-1" />
                        Approve All
                    </Button>
                )}
            </div>

            {/* Items */}
            {category === "duplicate"
                ? (isLoadingClusters
                    ? (
                        <div className="p-8 text-center text-text-secondary">
                            Loading duplicate clusters...
                        </div>
                    )
                    : (
                        <DuplicateClusterReviewView
                            clusters={clusterData?.clusters ?? []}
                        />
                    ))
                : isLoading
                ? (
                    <div className="p-8 text-center text-text-secondary">
                        Loading review items...
                    </div>
                )
                : items.length === 0
                ? (
                    <div className="p-12 text-center text-text-secondary bg-bg-secondary rounded-lg border border-border">
                        <Check
                            size={48}
                            className="mx-auto mb-3 text-green-500"
                        />
                        <p className="text-lg font-medium">All clear</p>
                        <p className="text-sm">No items pending review.</p>
                    </div>
                )
                : (
                    <div className="space-y-3">
                        {items.map((item, index) => (
                            <ReviewItemCard
                                key={item.id}
                                item={item}
                                isFocused={index === focusedIndex}
                                onApprove={(itemId) =>
                                    approveMutation.mutate(itemId)}
                                onReject={(itemId) => setRejectingId(itemId)}
                                onOverride={(reviewItem) =>
                                    setOverrideItem(reviewItem)}
                                onConfirmWatermark={(reviewItem) =>
                                    setCropTarget(reviewItem)}
                                onDenyWatermark={(itemId) =>
                                    denyWatermarkMutation.mutate(itemId)}
                            />
                        ))}
                    </div>
                )}

            <ConfirmModal
                isOpen={rejectingId !== null}
                onClose={() => setRejectingId(null)}
                onConfirm={() => {
                    if (rejectingId) rejectMutation.mutate(rejectingId);
                }}
                title="Reject Item"
                message="This will soft-delete the item and mark it as rejected. Are you sure?"
                variant="danger"
                confirmText="Reject"
            />

            <ReviewOverrideModal
                isOpen={overrideItem !== null}
                onClose={() => setOverrideItem(null)}
                onSave={handleOverrideSave}
                item={overrideItem}
                isSaving={overrideMutation.isPending}
            />

            <CropModal
                isOpen={cropTarget !== null}
                onClose={() => setCropTarget(null)}
                item={cropTarget
                    ? {
                        id: cropTarget.id,
                        media_type: cropTarget.media_type,
                        media_url: cropTarget.media_url,
                    }
                    : null}
                onCropComplete={() => {
                    // record the verdict only after the crop physically
                    // succeeds. confirmWatermark also flips the row out
                    // of REVIEW status so it leaves the queue
                    if (cropTarget) {
                        confirmWatermarkMutation.mutate(cropTarget.id);
                    }
                }}
            />

            <ConfirmModal
                isOpen={showClearConfirm}
                onClose={() => setShowClearConfirm(false)}
                onConfirm={() => clearQueueMutation.mutate()}
                title="Clear Review Queue"
                message={`This will approve all ${items.length} item${
                    items.length !== 1 ? "s" : ""
                } in the review queue. Are you sure?`}
                variant="warning"
                confirmText="Approve All"
            />
        </div>
    );
};
